// Contenido legal público (Términos de Uso, FAQ) en un solo lugar, como texto markdown,
// para no repartir estas cadenas largas dentro del JSX de las páginas que las renderizan
// (Terms.jsx / Faq.jsx, con react-markdown + remark-gfm para la tabla de precios).
//
// TERMS_PUBLISHED controla si /terminos muestra el texto real o un aviso de "en preparación".
// El texto sigue teniendo 3 datos entre corchetes sin confirmar (razon social, domicilio,
// ciudad/estado de jurisdiccion en la Seccion 13) que dependen de datos reales de la empresa
// y de que el abogado confirme la clausula de responsabilidad de la Seccion 9. NO cambiar
// este flag a `true` hasta que el usuario confirme explicitamente que esos corchetes ya
// estan resueltos. (2026-08-30: ya se retiro del texto publico la nota interna dirigida al
// abogado revisor que vivia dentro de la Seccion 9; el punto de fondo -- si el tope de 12
// meses es correcto y si aplica la Ley Federal de Proteccion al Consumidor -- sigue abierto,
// solo que ya no esta redactado como comentario de borrador en un documento publico.)
export const TERMS_PUBLISHED = true;

// Debe coincidir con CURRENT_TERMS_VERSION en backend/routers/auth_router.py — es lo que se
// registra como evidencia de aceptación al crear una cuenta.
export const TERMS_VERSION = "v1.0";

export const TERMS_CONTENT = `# CERH — Términos de Uso

**Última actualización:** 19 de agosto de 2026 — **Versión 1.0**

## 1. Aceptación de los Términos

Al registrar una cuenta, contratar un plan, o utilizar CERH en cualquier forma, usted ("el Cliente", "la Empresa") acepta quedar obligado por estos Términos de Uso y por el Aviso de Privacidad de CERH, disponible en cerh.com.mx/aviso-privacidad. Si no está de acuerdo con alguna disposición de estos Términos, no debe registrar una cuenta ni utilizar el Servicio.

CERH es operado por Payne Administrativo, S.A. de C.V. (RFC PAD1905301TA), con domicilio en Calle Renato Leduc No. 174, Int. 301, Col. Toriello Guerra, Alcaldía Tlalpan, C.P. 14050, Ciudad de México, México ("CERH", "nosotros").

## 2. Descripción del Servicio

CERH es una plataforma de software como servicio (SaaS) para documentación de cumplimiento laboral y gestión de talento, dirigida a pequeñas y medianas empresas mexicanas. El Servicio incluye, entre otras funciones: expedientes de empleados, biblioteca de políticas y acuses, registro de vacaciones e incidencias, actas administrativas, registro de jornada laboral, centros de trabajo, capacitación, avisos, y un asesor de inteligencia artificial para temas de recursos humanos.

## 3. Registro y Cuenta

Para usar CERH, la Empresa debe registrar una cuenta de administrador proporcionando información veraz y completa. La Empresa es responsable de mantener la confidencialidad de sus credenciales de acceso y de toda actividad que ocurra bajo su cuenta, incluyendo las cuentas de empleados que la Empresa dé de alta dentro de la plataforma.

La Empresa es responsable de contar con el consentimiento o base legal adecuada para registrar los datos personales de sus empleados dentro de CERH, conforme a la legislación aplicable en materia de protección de datos personales.

## 4. Planes y Precios

CERH cobra por banda según el número de empleados activos registrados en la cuenta, sin capa gratuita. Los precios vigentes al momento de la publicación de estos Términos son:

| Banda de empleados activos | Precio mensual |
|---|---|
| 1 a 10 empleados | $450 MXN/mes |
| 11 a 15 empleados | $960 MXN/mes |
| 16 a 20 empleados | $1,260 MXN/mes |
| 21 a 30 empleados | $1,860 MXN/mes |
| 31 a 50 empleados | $3,000 MXN/mes |
| 51+ empleados | Plan a medida (Contáctanos) |

Las seis bandas incluyen exactamente las mismas funciones; el precio varía únicamente según el número de empleados activos, nunca según funcionalidad. Se ofrece un descuento del 10% al contratar el plan anual en lugar del mensual.

La banda de precio se recalcula automáticamente conforme cambia el número de empleados activos de la Empresa:

- Si el número de empleados activos SUBE de banda, el cambio de precio aplica de inmediato, con prorrateo del monto correspondiente al periodo restante del ciclo de facturación en curso.
- Si el número de empleados activos BAJA de banda, el nuevo precio, más bajo, aplica hasta el siguiente ciclo de facturación, no de inmediato.

## 5. Facturación y Pagos

Los pagos se procesan a través de Stripe, Inc., un proveedor externo de procesamiento de pagos. CERH no almacena directamente los datos completos de tarjetas de pago. Al proporcionar un método de pago, el Cliente autoriza a CERH a realizar cargos recurrentes conforme al plan y periodicidad (mensual o anual) que haya seleccionado.

Si un cargo recurrente falla, CERH otorgará un periodo de gracia de 7 días naturales para regularizar el método de pago. Transcurrido ese plazo sin que el pago se regularice, el acceso de la cuenta pasará a modo de solo lectura, conservando los datos existentes pero restringiendo la creación o edición de información hasta que el pago se regularice.

## 6. Cancelación

El Cliente puede cancelar su suscripción en cualquier momento desde el panel de Facturación. La cancelación se procesa como cancel_at_period_end: el Servicio permanece activo y facturado hasta el final del periodo ya pagado, y no se renovará al finalizar dicho periodo. CERH no realiza cancelaciones inmediatas con reembolso proporcional del periodo en curso, salvo que la ley aplicable exija lo contrario.

## 7. Uso Aceptable

El Cliente se compromete a utilizar CERH únicamente para fines lícitos relacionados con la administración de su propio personal, y a no:

- Utilizar el Servicio para almacenar o procesar datos de personas que no sean empleados, colaboradores o candidatos de la propia Empresa.
- Intentar vulnerar, sobrecargar o acceder sin autorización a la infraestructura, código o datos de CERH o de otros clientes.
- Revender, sublicenciar o poner el Servicio a disposición de terceros ajenos a la Empresa contratante, salvo autorización expresa de CERH.

## 8. Propiedad Intelectual

El software, diseño, marca y contenidos de CERH son propiedad de CERH o de sus licenciantes. Estos Términos no transfieren al Cliente ningún derecho de propiedad intelectual sobre la plataforma. Los datos que el Cliente cargue a CERH (expedientes, documentos, información de empleados) siguen siendo propiedad del Cliente; CERH los trata conforme al Aviso de Privacidad.

## 9. Limitación de Responsabilidad

En la máxima medida permitida por la ley aplicable, CERH no será responsable por daños indirectos, incidentales, especiales o consecuentes derivados del uso o la imposibilidad de uso del Servicio, incluyendo decisiones laborales tomadas con apoyo de la plataforma. El Servicio se proporciona "tal cual" y "según disponibilidad".

La responsabilidad total de CERH frente al Cliente, por cualquier reclamación derivada de estos Términos, no excederá el monto pagado o por pagar por el Cliente a CERH durante los 12 meses anteriores al hecho que dio origen a la reclamación. Esta limitación no aplica a la responsabilidad derivada de dolo, la cual es exigible en todo caso conforme al artículo 2106 del Código Civil Federal.

La Ley Federal de Protección al Consumidor aplica a la relación entre CERH y el Cliente. Ninguna disposición de estos Términos, incluyendo la limitación de responsabilidad de esta Sección, pretende limitar o excluir los derechos irrenunciables que dicha ley reconoce al Cliente; en caso de conflicto entre esta Sección y una disposición de orden público de la Ley Federal de Protección al Consumidor, prevalece esta última.

Los documentos, cálculos y recomendaciones generados por el sistema son apoyos operativos y no sustituyen asesoría legal o contable profesional.

## 10. Protección de Datos Personales

El tratamiento de datos personales de la Empresa, sus administradores y sus empleados se rige por el Aviso de Privacidad de CERH, disponible en cerh.com.mx/aviso-privacidad, el cual forma parte integral de estos Términos.

## 11. Modificaciones a estos Términos

CERH podrá modificar estos Términos de Uso en cualquier momento. Los cambios entrarán en vigor a partir de su publicación en cerh.com.mx/terminos, con indicación de la fecha de última actualización. El uso continuado del Servicio después de una modificación constituye la aceptación de los Términos actualizados.

## 12. Terminación del Servicio

CERH podrá suspender o terminar el acceso de una cuenta que incumpla estos Términos, incluyendo el impago sostenido más allá del periodo de gracia descrito en la Sección 6, o el uso indebido descrito en la Sección 7. Al terminar el Servicio, la Empresa podrá solicitar la exportación de sus datos dentro de un plazo razonable antes de su eliminación definitiva, salvo obligación legal de conservación distinta.

## 13. Ley Aplicable y Jurisdicción

Estos Términos se rigen por las leyes de los Estados Unidos Mexicanos. Para cualquier controversia derivada de estos Términos, las partes se someten a los tribunales competentes de la Ciudad de México, renunciando a cualquier otro fuero que pudiera corresponderles por razón de su domicilio presente o futuro.

## 14. Contacto

Para preguntas sobre estos Términos de Uso, contactar a contacto@cerh.com.mx.`;

export const FAQ_CONTENT = `# Preguntas frecuentes de CERH

## Precios y planes

**¿Cuánto cuesta CERH?**

El precio depende del número de empleados registrados en la plataforma. Los planes van de $450 a $3,000 MXN al mes según el rango de empleados. Para empresas con más de 51 empleados, el precio se cotiza directamente con el equipo de CERH.

**¿Hay un plan gratuito?**

No. CERH no tiene un nivel gratuito permanente.

**¿Hay descuento por pago anual?**

Sí, 10% de descuento sobre el precio mensual si se paga el plan anual por adelantado.

**¿Qué pasa si contrato más o menos empleados durante el periodo de facturación?**

El plan se ajusta automáticamente al rango correspondiente. Si aumenta el número de empleados, el cambio de plan aplica de inmediato y se cobra la diferencia proporcional al periodo restante. Si disminuye, el ajuste a un plan menor aplica hasta el siguiente ciclo de facturación.

**¿Qué pasa si no pago a tiempo?**

Hay un periodo de gracia de 7 días después de la fecha de pago. Si el pago no se regulariza en ese plazo, la cuenta entra en modo de solo lectura hasta que se resuelva el pago.

## Cancelación

**¿Puedo cancelar cuando quiera?**

Sí, desde el panel de Facturación. La cancelación se procesa para el final del periodo ya pagado: el servicio sigue activo hasta esa fecha y no se renueva después. CERH no hace reembolsos proporcionales por cancelación anticipada, salvo que la ley aplicable exija lo contrario.

**¿Qué pasa con mis datos si cancelo?**

Los datos de la empresa (expedientes, documentos, información de empleados) siguen siendo propiedad del cliente. Al terminar el servicio, la empresa puede solicitar la exportación de sus datos antes de su eliminación definitiva.

## Inteligencia artificial en CERH

**¿Necesito conocimientos legales para usar CERH?**

No. Las plantillas de documentos que usa CERH (actas administrativas, avisos de rescisión, contratos) están construidas conforme a la Ley Federal del Trabajo y revisadas por un abogado laboral. Lo que varía caso por caso son los hechos específicos que tú capturas y las recomendaciones abiertas del Asesor de RH: para decisiones de alto impacto, como una rescisión, te recomendamos que un abogado revise el caso concreto antes de ejecutarlo, igual que harías con cualquier proceso de RH.

**¿Los documentos que genera CERH ya están listos para usarse?**

Cada documento se genera a partir de una plantilla construida conforme a la Ley Federal del Trabajo, con los datos que capturas en el expediente del empleado. Para acciones con consecuencias legales importantes, como una rescisión, recomendamos que un abogado laboral revise el caso específico antes de ejecutarla, igual que recomendaría cualquier práctica responsable de RH, con o sin software. El acta administrativa es una prueba documental del hecho que describe; su valor en un eventual conflicto depende también de que las partes la firmen, del contenido registrado y de las demás pruebas del caso. Recomendamos firmarla siempre que sea posible.

**¿Puedo confiar en los cálculos de finiquito o indemnización que muestra CERH?**

Son estimados basados en la información registrada en el portal (salario, antigüedad, vacaciones acumuladas), calculados con las fórmulas de la Ley Federal del Trabajo. CERH los marca explícitamente como sujetos a verificación de un contador o abogado laboral antes de tomar cualquier decisión.

## Seguridad y datos

**¿De quién son los datos que subo a CERH?**

De la empresa cliente. CERH los trata conforme a su Aviso de Privacidad y no los usa para fines distintos a prestar el servicio.

## Soporte

**¿Cómo contacto a soporte?**

Escribiendo a contacto@cerh.com.mx.`;

export const DPA_PUBLISHED = true;

export const DPA_CONTENT = `## 1. Objeto

Este Acuerdo de Tratamiento de Datos ("Acuerdo") complementa los Términos de Uso y el Aviso de Privacidad de CERH, y describe cómo CERH trata los datos personales que la Empresa cliente ("el Responsable") le encarga al usar la plataforma para administrar a su personal.

## 2. Partes y roles

Bajo la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), la Empresa cliente es el Responsable de los datos personales de sus empleados, colaboradores y candidatos que registra en CERH. CERH actúa como Encargado: trata esos datos únicamente por cuenta e instrucción del Responsable, para prestar el Servicio descrito en los Términos de Uso.

## 3. Instrucciones y alcance del tratamiento

CERH trata los datos personales de los empleados de la Empresa exclusivamente para las finalidades del Servicio: expedientes, políticas y acuses, vacaciones e incidencias, registro de jornada, nómina y capacitación, entre las demás funciones descritas en los Términos de Uso. CERH no usa esos datos para fines propios ajenos a la prestación del Servicio, ni los vende ni los comparte con terceros para fines de mercadotecnia.

## 4. Subencargados

Para operar el Servicio, CERH se apoya en los siguientes subencargados:

- Google Cloud Platform, que aloja la infraestructura de aplicación y base de datos en la región us-central1 (Council Bluffs, Iowa, Estados Unidos).
- Stripe, Inc., que procesa los pagos de la suscripción.
- Resend, que envía los correos automáticos de la plataforma.

Esto implica que los datos personales se almacenan y procesan fuera de México, en Estados Unidos. CERH se compromete a notificar a la Empresa si incorpora un nuevo subencargado con acceso a los datos de sus empleados.

## 5. Medidas de seguridad

CERH cifra los datos en tránsito mediante TLS/HTTPS y en reposo mediante el estándar AES-256, de forma automática. Los respaldos de la base de datos se generan de forma horaria, diaria, semanal y mensual, con recuperación a un punto exacto en el tiempo dentro de los últimos 7 días. El proveedor de infraestructura de CERH cuenta con certificaciones ISO 27001 y SOC 2.

## 6. Confidencialidad

El personal de CERH con acceso a los datos personales de los empleados de la Empresa está sujeto a obligaciones de confidencialidad, y ese acceso se limita a lo necesario para operar y dar soporte a la plataforma.

## 7. Incidentes de seguridad

Si CERH detecta un incidente que comprometa la confidencialidad, integridad o disponibilidad de los datos personales tratados, notificará a la Empresa sin dilación indebida, con la información disponible sobre la naturaleza del incidente y las medidas tomadas. La LFPDPPP no fija un plazo exacto para esta notificación (a diferencia de otras leyes de protección de datos); CERH aplica este estándar como buena práctica.

## 8. Solicitudes de derechos ARCO y de autoridad

Cuando un empleado de la Empresa dirija a CERH una solicitud relacionada con sus derechos ARCO, CERH la remitirá a la Empresa, responsable de atenderla conforme a su propio aviso de privacidad. Si una autoridad requiere a CERH información sobre los datos tratados por cuenta de la Empresa, CERH lo notificará a la Empresa salvo que la ley se lo prohíba expresamente.

## 9. Conservación y eliminación

Al terminar el Servicio, CERH conserva el expediente de la Empresa durante 90 días naturales para permitir su reactivación o la exportación de datos, conforme a lo descrito en el Aviso de Privacidad. Transcurrido ese plazo, la información se elimina de forma definitiva de los sistemas activos, sujeta al calendario de retención de los respaldos históricos descrito en la Sección 5.

## 10. Vigencia

Este Acuerdo tiene la misma vigencia que el contrato de servicio entre la Empresa y CERH, y termina junto con este, sin perjuicio de las obligaciones de conservación y eliminación descritas en la Sección 9.

## 11. Contacto

Para preguntas sobre este Acuerdo, contactar a contacto@cerh.com.mx.

---

Este Acuerdo se actualiza conforme a la infraestructura y las medidas de seguridad vigentes de CERH y su proveedor de hosting.`;
